import { renderWithLoader, useMutation } from '@meteor/data-hooks';
import { Attributes, Button, Dialog, Select, showModal, Spacer } from '@meteor/ui';
import { Card, DataList, Grid, Heading, Table } from '@radix-ui/themes';
import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { organizationPlanState, organizationState, plansState } from '../../../state';

export let OrganizationPage = () => {
  let { organizationId } = useParams();
  let organization = organizationState.use({ id: organizationId! });
  let organizationPlan = organizationPlanState.use({ organizationId: organizationId! });

  return renderWithLoader({ organization, organizationPlan })(
    ({ organization, organizationPlan }) => (
      <>
        <Heading as="h1" size="7">
          {organization.data.name}
        </Heading>

        <Heading as="h2" size="4" style={{ marginBottom: 10, marginTop: 20 }}>
          User Profile
        </Heading>

        <DataList.Root>
          {[
            ['Id', organization.data.id],
            ['Slug', organization.data.slug],
            ['Name', organization.data.name],
            ['Status', organization.data.status],
            ['Type', organization.data.type],
            ['Created At', organization.data.createdAt.toLocaleDateString('de-at')],
            ['Updated At', organization.data.updatedAt.toLocaleDateString('de-at')],
            ['Deleted At', organization.data.deletedAt?.toLocaleDateString('de-at')]
          ].map(([label, value]) => (
            <DataList.Item key={label}>
              <DataList.Label>{label}</DataList.Label>
              <DataList.Value>{value}</DataList.Value>
            </DataList.Item>
          ))}
        </DataList.Root>

        <Heading as="h2" size="4" style={{ marginBottom: 10, marginTop: 20 }}>
          Members
        </Heading>

        <Table.Root variant="surface">
          <Table.Header>
            <Table.Row>
              <Table.ColumnHeaderCell>Name</Table.ColumnHeaderCell>
              <Table.ColumnHeaderCell>Email</Table.ColumnHeaderCell>
              <Table.ColumnHeaderCell>Created At</Table.ColumnHeaderCell>
              <Table.ColumnHeaderCell></Table.ColumnHeaderCell>
            </Table.Row>
          </Table.Header>

          <Table.Body>
            {organization.data.members.map(member => (
              <Table.Row key={member.id}>
                <Table.Cell>{member.name}</Table.Cell>
                <Table.Cell>{member.email}</Table.Cell>
                <Table.Cell>{member.createdAt.toLocaleDateString('de-at')}</Table.Cell>
                <Table.Cell>
                  <Link to={`/users/${member.user?.id}`}>
                    <Button as="span" size="1">
                      View
                    </Button>
                  </Link>
                </Table.Cell>
              </Table.Row>
            ))}
          </Table.Body>
        </Table.Root>

        <Heading as="h2" size="4" style={{ marginBottom: 10, marginTop: 20 }}>
          Billing
        </Heading>

        <Attributes
          attributes={[
            { label: 'Plan Name', content: organizationPlan.data.name },
            { label: 'Plan Slug', content: organizationPlan.data.slug },
            { label: 'Plan ID', content: organizationPlan.data.id }
          ]}
        />

        <Spacer height={15} />

        <Button
          onClick={() =>
            showModal(({ dialogProps, close }) => {
              let update = useMutation(organizationPlan.mutators.setOrganizationPlan);
              let plans = plansState.use();

              let [planId, setPlanId] = useState<string>(organizationPlan.data.id);

              return (
                <Dialog.Wrapper {...dialogProps}>
                  <Dialog.Title>Set Plan</Dialog.Title>

                  <Select
                    label="Plan"
                    items={plans.data?.map(p => ({ id: p.id, label: p.name })) ?? []}
                    value={planId}
                    onChange={setPlanId}
                  />

                  <Spacer height={15} />

                  <Dialog.Actions>
                    <Button onClick={close} variant="outline">
                      Cancel
                    </Button>

                    <Button
                      onClick={() => {
                        update.mutate({ organizationId: organizationId!, planId });
                        close();
                      }}
                    >
                      Set Plan
                    </Button>
                  </Dialog.Actions>
                </Dialog.Wrapper>
              );
            })
          }
        >
          Set Plan
        </Button>

        <Heading as="h2" size="4" style={{ marginBottom: 10, marginTop: 20 }}>
          Projects
        </Heading>

        <Grid columns="2" gap="2">
          {organization.data.projects.map(project => (
            <Card key={project.id}>
              <Heading as="h3" size="4">
                {project.name}
              </Heading>

              <DataList.Root>
                {[
                  ['ID', project.id],
                  ['Name', project.name],
                  ['Slug', project.slug],
                  ['Created At', project.createdAt.toLocaleDateString('de-at')],
                  ['Updated At', project.updatedAt.toLocaleDateString('de-at')],
                  ['Deleted At', project.deletedAt?.toLocaleDateString('de-at')]
                ].map(([label, value]) => (
                  <DataList.Item key={label}>
                    <DataList.Label>{label}</DataList.Label>
                    <DataList.Value>{value}</DataList.Value>
                  </DataList.Item>
                ))}
              </DataList.Root>

              <Heading as="h4" size="2" style={{ marginBottom: 10, marginTop: 20 }}>
                Instances
              </Heading>

              <Table.Root variant="surface">
                <Table.Header>
                  <Table.Row>
                    <Table.ColumnHeaderCell>ID</Table.ColumnHeaderCell>
                    <Table.ColumnHeaderCell>Name</Table.ColumnHeaderCell>
                    <Table.ColumnHeaderCell>Slug</Table.ColumnHeaderCell>
                    <Table.ColumnHeaderCell>Created At</Table.ColumnHeaderCell>
                    <Table.ColumnHeaderCell></Table.ColumnHeaderCell>
                  </Table.Row>
                </Table.Header>

                <Table.Body>
                  {project.instances.map(instance => (
                    <Table.Row key={instance.id}>
                      <Table.Cell>{instance.id}</Table.Cell>
                      <Table.Cell>{instance.name}</Table.Cell>
                      <Table.Cell>{instance.slug}</Table.Cell>
                      <Table.Cell>{instance.createdAt.toLocaleDateString('de-at')}</Table.Cell>
                    </Table.Row>
                  ))}
                </Table.Body>
              </Table.Root>
            </Card>
          ))}
        </Grid>
      </>
    )
  );
};
